import type { CepNleOps, CepNleScripts } from '@mimir/primitive/nle/cep/cepNleOps';
import { NleEventType } from '@mimir/primitive/nle/event';
import { addEventListener, evalRawScript } from './cep';
import type { NleOps } from '@mimir/primitive/nle/nleOps';
import type { NleEventBody } from '@mimir/primitive/nle/event';
import { initializePpro, PPRO_BUNDLE_PATH } from './initialization';
import { createSourceLocationResolver } from './extendScriptSourceLocation';
import type { SourceLocationResolver } from './extendScriptSourceLocation';
import { extendScriptCallError, parseExtendScriptCallReply } from './extendScriptError';

// ── CepNleOps ──────────────────------────────────────────────────────────────
export function createCepNleOps(): CepNleOps {
  const eventHandlers = new Map<NleEventType, (msg: NleEventBody) => void>();
  const resolveSourceLocation = createSourceLocationResolver(PPRO_BUNDLE_PATH);

  function dispatch(type: NleEventType, msg: NleEventBody): void {
    eventHandlers.get(type)?.(msg);
  }

  // Add listeners for all known event types
  // Panel can't be reloaded in production. We can assume that the
  // event handlers are only registered once per panel instance.
  for (const type of Object.values(NleEventType)) {
    addEventListener(type, (msg: NleEventBody) => dispatch(type, msg));
  }

  // Return a Proxy that routes method calls to evalTS, and onEvent to the eventHandlers map
  return new Proxy({} as CepNleOps, {
    get(_, method: string) {
      if (method === 'nleHostId') {
        return 'premiere-cep';
      }
      if (method === 'onEvent') {
        return <T extends NleEventType>(type: T, handler: (msg: NleEventBody<T>) => void): () => void => {
          eventHandlers.set(type, handler as (msg: NleEventBody) => void);
          return () => {
            evalTS(resolveSourceLocation, 'unbindPremiereAppEvent', [type]);
            eventHandlers.delete(type);
          }
        };
      }
      // Proxy erases the key type; CepNleOps typing at the call site already
      // guarantees that method is a CepNleScripts key.
      const key = method as keyof CepNleScripts;
      return (...args: Parameters<CepNleScripts[typeof key]>) => evalTS(resolveSourceLocation, key, args);
    },
  });
}

// ── evalTS ───────────────────────────────────────────────────────────────────

// Dispatch, ignoreProjectChanges handling and error serialization all live in
// ppro.call (extendscript/hosts/ppro.ts), so every error line it reports is a
// ppro.jsx line that the bundle's line map can resolve.
async function evalTS<K extends keyof CepNleScripts>(
  resolveSourceLocation: SourceLocationResolver,
  method: K,
  args: Parameters<CepNleScripts[K]>,
): Promise<ReturnType<CepNleScripts[K]>> {
  await initializePpro();

  const callScript = '$.global.ppro.call(' + JSON.stringify(method) + ',' + JSON.stringify(args) + ');';
  const reply = await evalRawScript(callScript);
  const obj = parseExtendScriptCallReply(reply, `ppro.call('${method}')`);
  if (obj.error) {
    throw await extendScriptCallError(obj.error, method, args, resolveSourceLocation);
  }
  return obj.result as ReturnType<CepNleScripts[K]>;
}

export function isCepNleOps(nle: NleOps): nle is CepNleOps {
  return nle.nleHostId === 'premiere-cep';
}
